/**
 * Benutzerverwaltung (B-S1/R55) in den Einstellungen. Admin: anlegen, Rolle, deaktivieren, Passwort zurücksetzen –
 * jede Änderung steht im Protokoll (Audit). Leitung sieht die Liste nur lesend. Passwortregel: Mindestlänge.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PASSWORT_MIN_LAENGE, ROLLEN, type BenutzerZeile, type Rolle } from '@storeos/contracts';
import { benutzerAendern, benutzerAnlegen, benutzerListe, benutzerPasswort, eigenesPasswort } from '../api';
import { datumZeit } from '../components/format';
import { Dialog, DialogFormular, Feld, Karte, Tabelle, useBenutzer, useDarf, useToast } from '../components/ui';

const ROLLE_TEXT: Record<Rolle, string> = { lager: 'Lager', leitung: 'Leitung', admin: 'Admin', produktion: 'Produktion (B&B)' };

/** Leitung: Liste aus /einstellungen (ohne Admin-Route). Admin: volle Verwaltung. */
export function BenutzerKarte({ nurLesend }: { nurLesend: Array<{ id: string; name: string; email: string; rolle: Rolle; aktiv: boolean }> }) {
  const darf = useDarf();
  const ich = useBenutzer();
  const qc = useQueryClient();
  const toast = useToast();
  const admin = darf('admin');
  const q = useQuery({ queryKey: ['benutzer'], queryFn: benutzerListe, enabled: admin });
  const [dialog, setDialog] = useState<null | { art: 'neu' } | { art: 'passwort'; u: BenutzerZeile } | { art: 'eigenes' }>(null);
  const neu = () => { void qc.invalidateQueries({ queryKey: ['benutzer'] }); void qc.invalidateQueries({ queryKey: ['einstellungen'] }); };

  async function aendern(u: BenutzerZeile, d: { rolle?: Rolle; aktiv?: boolean }) {
    try {
      await benutzerAendern(u.id, d);
      toast(d.aktiv === false ? `${u.name} deaktiviert, alle Sitzungen beendet` : d.aktiv ? `${u.name} wieder aktiv` : `${u.name}: Rolle ${ROLLE_TEXT[d.rolle!]}`);
      neu();
    } catch (e) { toast((e as Error).message, 'fehler'); }
  }

  const zeilen = admin ? (q.data ?? []) : nurLesend;
  return (
    <Karte klasse="ganze-breite" titel={`Benutzer (${zeilen.length})`} aktion={
      <span className="knopfreihe links">
        <button type="button" className="knopf-zweit klein" onClick={() => setDialog({ art: 'eigenes' })} data-testid="eigenes-passwort">Eigenes Passwort</button>
        {admin && <button type="button" className="knopf klein" onClick={() => setDialog({ art: 'neu' })} data-testid="benutzer-neu">Benutzer anlegen</button>}
      </span>}>
      <Tabelle testid="benutzer-tabelle" zeilen={zeilen as BenutzerZeile[]} schluessel={(u) => u.id}
        spalten={[
          { kopf: 'Name', zelle: (u) => <span className={u.aktiv ? '' : 'hinweis'}>{u.name}{u.id === ich.id ? ' (du)' : ''}</span> },
          { kopf: 'E-Mail', zelle: (u) => u.email, klasse: 'mono' },
          { kopf: 'Rolle', zelle: (u) => (admin
            ? <select aria-label={`Rolle ${u.name}`} value={u.rolle} disabled={!u.aktiv} onChange={(e) => void aendern(u, { rolle: e.target.value as Rolle })}>{ROLLEN.map((r) => <option key={r} value={r}>{ROLLE_TEXT[r]}</option>)}</select>
            : ROLLE_TEXT[u.rolle]) },
          { kopf: 'Status', zelle: (u) => (u.aktiv ? <span className="badge modus-live">aktiv</span> : <span className="badge neutral">deaktiviert</span>) },
          ...(admin ? [
            { kopf: 'Letzter Login', zelle: (u: BenutzerZeile) => datumZeit(u.letzter_login_at), klasse: 'nebensache' },
            { kopf: '', zelle: (u: BenutzerZeile) => (
              <span className="knopfreihe links">
                <button type="button" className="knopf-flach" onClick={() => setDialog({ art: 'passwort', u })}>Passwort zurücksetzen</button>
                {u.id !== ich.id && (u.aktiv
                  ? <button type="button" className="knopf-flach warn-text" onClick={() => void aendern(u, { aktiv: false })}>Deaktivieren</button>
                  : <button type="button" className="knopf-flach" onClick={() => void aendern(u, { aktiv: true })}>Aktivieren</button>)}
              </span>) },
          ] : []),
        ]} />
      <p className="hinweis">Passwörter: mindestens {PASSWORT_MIN_LAENGE} Zeichen, sonst keine Regel (eine lange Passphrase ist am sichersten). Nach 5 Fehlversuchen ist ein Konto vorübergehend gesperrt. Jede Änderung steht im Protokoll.</p>

      <Dialog offen={dialog?.art === 'neu'} titel="Benutzer anlegen" onSchliessen={() => setDialog(null)}>
        {dialog?.art === 'neu' && <NeuFormular onAbbrechen={() => setDialog(null)} onFertig={(u) => { setDialog(null); toast(`${u.name} angelegt (${ROLLE_TEXT[u.rolle]})`); neu(); }} />}
      </Dialog>
      <Dialog offen={dialog?.art === 'passwort'} titel={dialog?.art === 'passwort' ? `Passwort zurücksetzen: ${dialog.u.name}` : ''} onSchliessen={() => setDialog(null)}>
        {dialog?.art === 'passwort' && <PasswortFormular onAbbrechen={() => setDialog(null)} onAbsenden={async (p) => { await benutzerPasswort(dialog.u.id, p); setDialog(null); toast(`Neues Passwort für ${dialog.u.name} gesetzt, alle Sitzungen beendet`); neu(); }} />}
      </Dialog>
      <Dialog offen={dialog?.art === 'eigenes'} titel="Eigenes Passwort ändern" onSchliessen={() => setDialog(null)}>
        {dialog?.art === 'eigenes' && <PasswortFormular mitAltem onAbbrechen={() => setDialog(null)} onAbsenden={async (p, alt) => { await eigenesPasswort(alt ?? '', p); setDialog(null); toast('Passwort geändert'); }} />}
      </Dialog>
    </Karte>
  );
}

function NeuFormular({ onAbbrechen, onFertig }: { onAbbrechen: () => void; onFertig: (u: BenutzerZeile) => void }) {
  const [w, setW] = useState({ name: '', email: '', rolle: 'lager' as Rolle, passwort: '' });
  return (
    <DialogFormular absendenText="Anlegen" onAbbrechen={onAbbrechen} onAbsenden={async () => onFertig(await benutzerAnlegen(w))}>
      <Feld label="Name"><input value={w.name} onChange={(e) => setW({ ...w, name: e.target.value })} data-testid="neu-name" /></Feld>
      <Feld label="E-Mail"><input type="email" value={w.email} onChange={(e) => setW({ ...w, email: e.target.value })} data-testid="neu-email" /></Feld>
      <Feld label="Rolle">
        <select value={w.rolle} onChange={(e) => setW({ ...w, rolle: e.target.value as Rolle })} data-testid="neu-rolle">{ROLLEN.map((r) => <option key={r} value={r}>{ROLLE_TEXT[r]}</option>)}</select>
      </Feld>
      <Feld label="Erstes Passwort" hinweis={`mindestens ${PASSWORT_MIN_LAENGE} Zeichen · ${w.passwort.length} eingegeben`}>
        <input type="password" autoComplete="new-password" value={w.passwort} onChange={(e) => setW({ ...w, passwort: e.target.value })} data-testid="neu-passwort" />
      </Feld>
    </DialogFormular>
  );
}

function PasswortFormular({ mitAltem, onAbbrechen, onAbsenden }: { mitAltem?: boolean; onAbbrechen: () => void; onAbsenden: (neu: string, alt?: string) => Promise<void> }) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [wdh, setWdh] = useState('');
  return (
    <DialogFormular absendenText="Speichern" sperre={neu.length < PASSWORT_MIN_LAENGE || neu !== wdh} onAbbrechen={onAbbrechen} onAbsenden={() => onAbsenden(neu, alt)}>
      {mitAltem && <Feld label="Bisheriges Passwort"><input type="password" autoComplete="current-password" value={alt} onChange={(e) => setAlt(e.target.value)} /></Feld>}
      <Feld label="Neues Passwort" hinweis={`mindestens ${PASSWORT_MIN_LAENGE} Zeichen · ${neu.length} eingegeben`}><input type="password" autoComplete="new-password" value={neu} onChange={(e) => setNeu(e.target.value)} /></Feld>
      <Feld label="Wiederholen" hinweis={wdh && neu !== wdh ? 'stimmt nicht überein' : undefined}><input type="password" autoComplete="new-password" value={wdh} onChange={(e) => setWdh(e.target.value)} /></Feld>
    </DialogFormular>
  );
}
